// ============================================
// PUSH NOTIFICATIONS TYPES
// ============================================

/**
 * Consolidated categories (2026-08). `transactional` (Account updates) is
 * always on while the master toggle is on — it has no independent toggle and
 * is described on the master toggle itself. The other three are the optional
 * sub-categories users see in Profile → Notifications, all on by default.
 *
 * Legacy keys (`engagement`, `milestone`, `reminder`, `content`, `practice`)
 * may still exist on stored user docs; the server maps them:
 * milestone → insights; engagement/content/practice → encouragement;
 * reminder → events (see functions-enough/notifications/orchestrator.py).
 */
export type NotificationCategory =
  | 'insights'
  | 'encouragement'
  | 'events'
  | 'transactional';

/** The categories users can toggle individually (transactional is master-only). */
export const OPTIONAL_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  'insights',
  'encouragement',
  'events',
];

export type NotificationTriggerType =
  | 'scheduled'
  | 'event'
  | 'dataUpdate'
  | 'manual'
  /**
   * Copy-only template for a push whose timing/audience is owned by app code
   * (insight nudges, cart-vault reminders, no-spend pushes). Seeded lazily by
   * the server from `functions-enough/notifications/code_templates.py`; admins
   * edit title/body (and can test-send), but cannot schedule or blast it.
   * Disabling or deleting the doc reverts to the shipped in-code copy — it
   * does not stop the push.
   */
  | 'code';

/** Optional template subtype for admin presets and filtering */
export type NotificationTemplateKind =
  | 'generic'
  | 'noSpendWeekendAnnouncement';

/** Filter recipients by No-Spend Weekend block session state */
export type NoSpendSessionTargeting = 'any' | 'not_active';

export type NotificationEventName =
  | 'account_connected'
  | 'exercise_completed'
  | 'goal_reached'
  | 'streak_milestone'
  | 'new_transactions'
  | 'score_milestone'
  | 'subscription_started'
  | 'daily_content_ready';

export type ScheduleType = 'cron' | 'daysSinceSignup' | 'monthlyNthWeekday';

export interface NotificationSchedule {
  /** Schedule type — defaults to 'cron' for backward compatibility */
  type?: ScheduleType;
  /** Cron expression, e.g. "0 9 * * *" (used when type is 'cron' or omitted) */
  cron?: string;
  /** Timezone — "America/New_York" or "user_local" for per-user delivery */
  timezone?: string;
  /** Number of days after signup to send (used when type is 'daysSinceSignup') */
  daysSinceSignup?: number;
  /** Hour of day (0-23) to send the notification (used with daysSinceSignup / monthlyNthWeekday) */
  sendAtHour?: number;
  /**
   * Nth occurrence of a weekday in the month (1–4), e.g. 3 = third Thursday.
   * Used when type is `monthlyNthWeekday`.
   */
  weekOfMonth?: number;
  /**
   * Weekday to match: 0 = Monday … 6 = Sunday (matches Python `datetime.weekday()`).
   * Used when type is `monthlyNthWeekday`.
   */
  dayOfWeek?: number;
}

/** Quiet hours window — notifications are held until the window ends */
export interface QuietHoursConfig {
  enabled: boolean;
  /** Start time in HH:MM format, e.g. "21:00" */
  start: string;
  /** End time in HH:MM format, e.g. "09:00" */
  end: string;
  /** Fallback timezone when user's local timezone is unknown */
  fallbackTimezone: string;
}

export interface NotificationEventTrigger {
  eventName: NotificationEventName;
  /** Optional delay in minutes after event before sending */
  delayMinutes?: number;
  /** Quiet hours — hold delivery during overnight window */
  quietHours?: QuietHoursConfig;
}

export interface NotificationDataUpdateTrigger {
  /** Firestore collection to watch */
  collection: string;
  /** Specific field change to watch */
  field?: string;
  /** Condition expression, e.g. "newTransactionsCount > 0" */
  condition?: string;
  /** Quiet hours — hold delivery during overnight window */
  quietHours?: QuietHoursConfig;
}

export interface NotificationTargeting {
  allUsers: boolean;
  connectionTypes?: string[];
  subscriptionStatus?: 'free' | 'subscribed' | 'any';
  /** Target users inactive for at least N days */
  inactiveDays?: number;
  segments?: string[];
  /**
   * When `not_active`, skip users with an active No-Spend Weekend block session
   * (`userContent/{uid}/blocking/noSpendWeekend.activeSession` with future endsAt).
   */
  noSpendSession?: NoSpendSessionTargeting;
  /**
   * Control share, 1–99. That percent of the eligible audience is held back
   * (recorded in `notificationHoldouts`, never sent) so the template reads as
   * sent-vs-held. Assignment is a stable hash of (holdoutSalt ?? template id, uid).
   */
  holdoutPercent?: number;
  /** Override the hash salt to re-randomize the holdout arms. */
  holdoutSalt?: string;
}

export interface NotificationTemplate {
  id: string;
  title: string;
  /** Supports {{variables}} like {{firstName}}, {{wellnessScore}} */
  body: string;
  category: NotificationCategory;
  /** Human note shown in the admin editor — for code templates it documents
   *  when the push fires and which extra {{variables}} the sender supplies. */
  description?: string;
  /** True for server-seeded code-template docs (triggerType 'code'). */
  codeManaged?: boolean;
  /** Preset subtype — e.g. no-spend weekend announcement */
  templateKind?: NotificationTemplateKind;
  triggerType: NotificationTriggerType;
  schedule?: NotificationSchedule;
  eventTrigger?: NotificationEventTrigger;
  dataUpdateTrigger?: NotificationDataUpdateTrigger;
  targeting?: NotificationTargeting;
  /** Custom data payload for deep linking — e.g. { screen: "reflect", itemId: "abc" } */
  data?: Record<string, string>;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export type NotificationLogStatus = 'sent' | 'partial' | 'failed';

export interface NotificationLogEntry {
  id: string;
  templateId: string;
  title: string;
  body: string;
  triggerType: NotificationTriggerType;
  recipientCount: number;
  sentAt: string;
  status: NotificationLogStatus;
  errors?: string[];
  sentBy?: string;
}

export interface FcmTokenEntry {
  token: string;
  platform: 'ios' | 'android' | 'web';
  updatedAt: string;
  createdAt: string;
}

export interface NotificationPreferences {
  enabled: boolean;
  categories: Record<NotificationCategory, boolean>;
  /**
   * How often the user wants to hear from us at most, across all push types.
   * Enforced by the server-side orchestration gate (notifications/orchestrator.py):
   * 'daily' ≈ one push per day, 'weekly' ≈ one per week. Unset = no cadence
   * constraint beyond the global frequency caps. Critical, opted-into pushes
   * (e.g. no-spend window start) and user-initiated sends are exempt.
   */
  cadence?: 'daily' | 'weekly';
  quietHours?: {
    start: string; // "22:00"
    end: string;   // "08:00"
  };
  /** IANA timezone stamped by the client at token save so servers can evaluate
   *  quiet hours in the user's local time (they fall back to US Eastern). */
  timezone?: string;
}

/** Default notification preferences for new users — everything on. */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  enabled: true,
  categories: {
    insights: true,
    encouragement: true,
    events: true,
    transactional: true,
  },
};

/**
 * Legacy stored-preference keys → consolidated category. Mirrored by
 * functions-enough/notifications/orchestrator.py — keep the two in sync.
 */
export const LEGACY_NOTIFICATION_CATEGORY_MAP: Record<string, NotificationCategory> = {
  engagement: 'encouragement',
  milestone: 'insights',
  reminder: 'events',
  content: 'encouragement',
  practice: 'encouragement',
};

/**
 * Effective on/off for a category against a stored categories map that may
 * predate the consolidation. New key wins; otherwise a legacy key that maps
 * to it carries the user's old choice; absent everywhere = on (all
 * categories default on when push is first enabled). `transactional` is
 * always on — it rides the master toggle.
 */
export function resolveCategoryPreference(
  categories: Partial<Record<string, boolean>> | undefined,
  category: NotificationCategory,
): boolean {
  if (category === 'transactional') return true;
  if (!categories) return true;
  if (category in categories) return categories[category] !== false;
  for (const [legacy, mapped] of Object.entries(LEGACY_NOTIFICATION_CATEGORY_MAP)) {
    if (mapped === category && legacy in categories) return categories[legacy] !== false;
  }
  return true;
}

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  insights: 'Personal insights',
  encouragement: 'Encouragement',
  events: 'Events',
  transactional: 'Account updates',
};

export const NOTIFICATION_CATEGORY_DESCRIPTIONS: Record<NotificationCategory, string> = {
  insights: 'Updates on your spending and habits',
  encouragement: 'Reminders and milestone celebrations',
  events: 'Time-sensitive notes about Pauses or community events',
  transactional: 'Updates about your account — included with push notifications',
};
